import * as FullStory from "@fullstory/browser";
import { get as lsGet, remove as lsRemove, set as lsSet } from "local-storage";
import { clearAll } from "@/utils/networkCache";
import constants from "@/utils/constants";
import inEu from "@segment/in-eu";
import NavLink from "@/classes/NavLink";
import { request } from "leatherman-js";
import Status from "@/classes/Status";
import { switchClientAction } from "@/utils/switchClient";
import Token from "leatherman-js/models/token";

const tokenName = constants.AUTH_TOKEN_KEY;
const initToken = lsGet(tokenName);

const clientAnalyticsIdentify = function (user, client = {}) {
  try {
    const fullStoryPayload = {
      email: user?.email,
      clientId: client?.id,
      clientName: client?.name,
      cloudServerCount: client?.cloudServerCount,
      colocationCount: client?.colocationCount,
      dedicatedServerCount: client?.dedicatedServerCount,
      isPartner: client?.isPartner,
      planCount: client?.planCount,
      resellerHostingCount: client?.resellerHostingCount,
      sharedHostingCount: client?.sharedHostingCount,
      ticketCount: client?.ticketCount,
    };
    if (process.env.VUE_APP_FULLSTORY_ID) {
      FullStory.identify(user?.id, fullStoryPayload);
    }

    console.log("clientAnalytics", { user, client, fullStoryPayload });
  } catch (e) {
    console.log("analytics error", e);
  }
};

const auth = {
  namespaced: true,
  state: {
    token: lsGet(tokenName),
    user: null,
    client: null,
    clientOptions: null,
    authInterceptor: initToken?.fullString
      ? request.setAuthRequestHeader(initToken.fullString)
      : null,
    status: new Status(),
    switchStatus: new Status(),
    gdpr: inEu(),
  },
  mutations: {
    SET_USER(state, payload) {
      state.user = payload;
    },
    SET_CLIENT(state, payload) {
      state.client = payload;
    },
    SET_CLIENT_OPTIONS(state, payload) {
      state.clientOptions = payload;
    },
    SET_TOKEN(state, payload) {
      // console.log("a new token: ", payload);
      request.ejectInterceptor(state.authInterceptor);
      lsSet(tokenName, payload);
      state.authInterceptor = request.setAuthRequestHeader(payload.fullString);
      state.token = payload;
    },
    CLEAR_TOKEN(state) {
      request.ejectInterceptor(state.authInterceptor);
      lsRemove(tokenName);
      state.token = null;
    },
    CLEAR_USER(state) {
      state.user = null;
    },
    CLEAR_CLIENT(state) {
      state.client = null;
    },
    SET_SWITCH_STATUS(state, payload) {
      state.switchStatus.value = payload;
    },
    SET_STATUS(state, payload) {
      state.status.value = payload;
    },
  },
  actions: {
    setToken({ commit }, token) {
      commit("SET_TOKEN", new Token(token));
    },
    async login({ commit, dispatch }, payload) {
      // default is to check for 2fa requirement
      let run2fa = true;

      // if the payload has a code, then skip check for 2fa
      if (payload.twoFactorCode) {
        run2fa = false;
      }

      // default assuming 2fa is not required
      // then check for 2fa if needed
      if (run2fa) {
        const { twoFactorRequired, hasCell } = await request.makeParse("check2faByUsername", {
          username: payload.email,
        });

        if (twoFactorRequired) {
          return Promise.reject({ twoFactorRequired, hasCell });
        }
      }

      //attempt login
      const loginAttempt = await request.makeParse(
        "login",
        {},
        {
          username: payload.email,
          password: payload.password,
          twoFactorCode: payload.twoFactorCode,
          rememberMe: payload.rememberMe,
          recaptchaToken: payload.recaptchaToken,
        }
      );

      dispatch("analytics/send", { name: "login", data: loginAttempt.user }, { root: true });
      dispatch("cartstack/setEmail", loginAttempt.user.email, { root: true });

      // if login successful, set the state of the token
      commit("SET_TOKEN", loginAttempt.token);

      // don't return any response data - the state is handling that data
      return "Successful Login";
    },
    async loginAs({ commit }, token) {
      clearAll();
      // if login successful, set the state of the token
      commit("SET_TOKEN", new Token(token));
      // don't return any response data - the state is handling that data
      return "Successful Login";
    },
    async forgotPassword(context, username) {
      await request.makeParse("forgotPassword", {}, { username });
      return "Successfully request password reset";
    },
    async resetPassword(context, payload) {
      const { newPassword, confirmPassword, authToken, requestId, userId, twoFactorCode } = payload;

      // default is to check for 2fa requirement
      let run2fa = true;

      // if the payload has a code, then skip check for 2fa
      if (twoFactorCode) {
        run2fa = false;
      }

      // default assuming 2fa is not required
      // then check for 2fa if needed
      if (run2fa) {
        const { twoFactorRequired, hasCell } = await request.makeParse("check2faById", {
          id: userId,
        });

        if (twoFactorRequired) {
          return Promise.reject({ twoFactorRequired, hasCell });
        }
      }

      await request.makeParse(
        "resetPassword",
        { userId },
        { newPassword, confirmPassword, authToken, requestId, twoFactorCode }
      );
      return "Successfully request password reset";
    },
    check2fa(context, username) {
      return request.makeParse("check2faByUsername", { username });
    },
    async enable2fa(context, { code, secret }) {
      const response = await request.makeParse(
        "enable2fa",
        {},
        { code, secret, provider: "google" }
      );

      return response;
    },
    async disable2fa() {
      await request.makeParse("disable2fa", {}, { provider: "google" });

      return "Successfully deactivated 2fa";
    },
    send2faSMS(context, username) {
      return request.makeParse("send2faSMS", { username });
    },
    async generateBackupCodes() {
      const backupCodes = await request.makeParse("generateBackupCodes");

      return backupCodes;
    },
    async get2faInfo() {
      const response = await request.makeParse("get2faInfo");

      return response;
    },
    async logout({ commit, dispatch }) {
      await request.makeParse("logout");

      commit("CLEAR_TOKEN");
      commit("CLEAR_USER");
      commit("CLEAR_CLIENT");
      clearAll();

      dispatch("cartstack/reset", {}, { root: true });

      return Promise.resolve("Logged out");
    },
    async signup({ commit, dispatch }, payload) {
      try {
        await request.makeParse(
          "testCredentials",
          {
            email: payload.email,
            password: payload.password,
            acceptTerms: payload.acceptTerms ? "on" : "off",
          },
          {}
        );

        // if `testCredentials` is successful, a user already exists with that username
        // prevent the user from trying to signup
        return Promise.reject({
          request: {
            response: ["email: User Exists"],
          },
        });
      } catch (e) {
        // if `testCredentials` fails, no user exists with that username
        // continue with the signup process
        const signupAttempt = await request.makeParse("signup", {}, payload);

        // if signup successful, set the state of the token
        commit("SET_TOKEN", signupAttempt.token);
        // commit("SET_USER", signupAttempt.user);
        let userData = await dispatch("userInfo");

        // update cartstack
        dispatch("cartstack/setEmail", payload.email, { root: true });

        return Promise.resolve(userData);
      }
    },
    async userInfo({ commit }) {
      const user = await request.makeParse("getUser");
      commit("SET_USER", user);
      return user;
    },
    async clientInfo({ commit, state }, payload) {
      const client = await request.makeParse("getClient");

      commit("SET_CLIENT", client);
      if (payload?.reidentify) {
        clientAnalyticsIdentify(state.user, client);
      }

      return client;
    },
    async userClientInfo({ commit, dispatch }, payload) {
      commit("SET_STATUS", Status.LOADING);

      try {
        const [userInfo, clientInfo] = await Promise.all([
          dispatch("userInfo"),
          dispatch("clientInfo"),
        ]);

        commit("SET_STATUS", Status.LOADED);

        if (payload?.reidentify) {
          clientAnalyticsIdentify(userInfo, clientInfo);
        }

        return {
          user: userInfo,
          client: clientInfo,
        };
      } catch (e) {
        commit("CLEAR_TOKEN");
        commit("CLEAR_USER");
        commit("CLEAR_CLIENT");
        clearAll();
        dispatch("cartstack/reset", {}, { root: true });
        commit("SET_STATUS", Status.ERROR);
      }
    },
    async clientOptions({ commit }) {
      const clientOptions = await request.makeParse(
        "getClientOptions",
        {},
        {},
        { headers: { "Accept-Format": "nocworx/json-bundle" } }
      );
      commit("SET_CLIENT_OPTIONS", clientOptions);
      return clientOptions;
    },
    async editPin({ commit, state }, newPin) {
      const client = state.client;
      const response = await request.makeParse("updatePin", { id: client.id }, { pin: newPin });
      const updatedClient = {
        ...client,
        pin: response.pin,
      };
      commit("SET_CLIENT", updatedClient);
      return response;
    },
    async editDefaultPayment({ commit, state }, newType) {
      const client = state.client;
      const response = await request.makeParse(
        "updateDefaultPayment",
        { id: client.id },
        { paymentType: newType }
      );
      const updatedClient = {
        ...client,
        paymentType: response.payment_type,
      };
      commit("SET_CLIENT", updatedClient);
      return response;
    },
    editUser(
      { state },
      {
        id,
        roles,
        firstName,
        lastName,
        email,
        cell,
        password,
        passwordConfirm,
        currentPassword,
        countryCode,
      }
    ) {
      const actualId = id ?? state.user.id;

      return request.makeParse(
        "editUser",
        { id: actualId },
        {
          roles,
          firstName,
          lastName,
          email,
          cell,
          password,
          passwordConfirm,
          currentPassword,
          countryCode,
        }
      );
    },
    async join({ commit }, { clientId, userId, code }) {
      //attempt join
      const joinAttempt = await request.makeParse(
        "join",
        { userId },
        {
          clientId,
          code,
        }
      );

      commit("SET_TOKEN", joinAttempt.token);
      commit("SET_USER", joinAttempt.user);

      // don't return any response data - the state is handling that data
      return "Successful join";
    },
    async invite(context, payload) {
      await request.makeParse("invite", null, payload);

      return "Invited successfully";
    },
    async switchClient({ commit }, { clientId }) {
      commit("SET_SWITCH_STATUS", Status.LOADING);

      try {
        const switchAttempt = await request.makeParse("switchClient", null, { clientId });
        clearAll();

        commit("SET_SWITCH_STATUS", Status.LOADED);
        commit("SET_TOKEN", switchAttempt.token);
        commit("SET_USER", switchAttempt.user);

        return "Successful switch";
      } catch (e) {
        commit("SET_SWITCH_STATUS", Status.ERROR);
      }
    },
    async getSupportedCountries() {
      const countryList = request.makeParse("getSupportedCountries");
      return countryList;
    },
    async changeDefaultClient({ dispatch }, { id, clientId }) {
      await request.makeParse("changeDefaultClient", { id }, { clientId });
      await dispatch("userInfo");
      return "Updated Default";
    },
  },
  getters: {
    isAdmin(state) {
      return new Token(state.token?.fullString).isAdmin || false;
    },
    isBetaTester(state) {
      return state.user?.isBetaTester || false;
    },
    isAdminOrBeta(state) {
      return new Token(state.token?.fullString).isAdmin || state.user?.isBetaTester || false;
    },
    isLoggedIn(state) {
      return !!state.token;
    },
    isLoading(state) {
      return state.status.isLoading || state.status.isUpdating;
    },
    isSwitching(state) {
      return state.switchStatus.isLoading || state.switchStatus.isUpdating;
    },
    getUser(state) {
      return state.user;
    },
    getClient(state) {
      return state.client;
    },
    getClientOptions(state) {
      return state.clientOptions;
    },
    getToken(state) {
      return state.token;
    },
    getLoginableClients(state) {
      if (!state.user || !state.user.loginableClients) {
        return null;
      }

      return state.user.loginableClients.map(
        (item) =>
          new NavLink(item.identity, null, () => {
            switchClientAction(item.id);
          })
      );
    },
  },
};

export default auth;
